// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Contact from "./contact.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "notification_type": {
      "column": {
        "default": null,
        "name": "notification_type",
        "null": false,
        "type": "string"
      },
      "name": "notification_type",
      "selected_by_default": null,
      "translated": null
    },
    "parameters": {
      "column": {
        "default": null,
        "name": "parameters",
        "null": false,
        "type": "jsonb"
      },
      "name": "parameters",
      "selected_by_default": null,
      "translated": null
    },
    "read": {
      "column": {
        "default": false,
        "name": "read",
        "null": false,
        "type": "boolean"
      },
      "name": "read",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Notification",
  "collectionKey": "notifications",
  "collectionName": "notifications",
  "i18nKey": "notification",
  "camelizedLower": "notification",
  "name": "Notification",
  "nameDasherized": "notification",
  "pluralName": "notifications",
  "ransackable_associations": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "contact_id",
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "notification_type",
      "column": {
        "default": null,
        "name": "notification_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "old_contact_id",
      "column": {
        "default": null,
        "name": "old_contact_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "parameters",
      "column": {
        "default": null,
        "name": "parameters",
        "null": false,
        "type": "jsonb"
      }
    },
    {
      "name": "read",
      "column": {
        "default": false,
        "name": "read",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    }
  ],
  "paramKey": "notification",
  "primaryKey": "id"
}

/** Frontend model for Notification. */
export default class Notification extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  notificationType() {
    return this.readAttributeUnderscore("notification_type")
  }

  /** @returns {boolean} */
  hasNotificationType() {
    const value = this.notificationType()

    return this._isPresent(value)
  }

  /** @returns {Record<string, any> | Array<any>} */
  parameters() {
    return this.readAttributeUnderscore("parameters")
  }

  /** @returns {boolean} */
  hasParameters() {
    const value = this.parameters()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  read() {
    return this.readAttributeUnderscore("read")
  }

  /** @returns {boolean} */
  hasRead() {
    const value = this.read()

    return this._isPresent(value)
  }

  /** @returns {import("./contact.js").default | null} */
  contact() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "contact"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadContact() {
    if (!("contactId" in this)) throw new Error("Foreign key method wasn't defined: contactId")

    const id = this.contactId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  ensureContactLoaded() {
    return this.ensureAssociationLoaded("contact")
  }
}
